import React, { useState, useEffect } from 'react';
import authService from './api-authorization/AuthorizeService';
import axios from 'axios';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

const EVENT_TYPE_LABELS = {
    0: { label: 'Started', color: 'info' },
    1: { label: 'Succeeded', color: 'success' },
    2: { label: 'Failed', color: 'error' },
    3: { label: 'Skipped', color: 'default' },
    4: { label: 'Enabled', color: 'success' },
    5: { label: 'Disabled', color: 'warning' },
    6: { label: 'Run Once', color: 'primary' },
};

export default function CronJobs() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingJob, setSavingJob] = useState(null);
    const [runningJob, setRunningJob] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");

    // Logs
    const [logs, setLogs] = useState([]);
    const [logDays, setLogDays] = useState(7);
    const [logsLoading, setLogsLoading] = useState(false);
    const [purgeDays, setPurgeDays] = useState(30);
    const [purging, setPurging] = useState(false);

    const apiPath = '/api/CronJobs';

    const authHeaders = async () => {
        const t = await authService.getAccessToken();
        return { headers: { 'Authorization': `Bearer ${t}` } };
    };

    const loadJobs = async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const headers = await authHeaders();
            const response = await axios.get(apiPath, headers);
            setJobs(response.data);
        } catch (error) {
            setErrorMessage(error.response?.data || error.message || "Failed to load cron jobs.");
        } finally {
            setLoading(false);
        }
    };

    const loadLogs = async (days) => {
        setLogsLoading(true);
        setErrorMessage("");
        try {
            const headers = await authHeaders();
            const response = await axios.get(`${apiPath}/logs?days=${days}`, headers);
            setLogs(response.data);
        } catch (error) {
            setErrorMessage(error.response?.data || error.message || "Failed to load logs.");
        } finally {
            setLogsLoading(false);
        }
    };

    useEffect(() => {
        loadJobs();
        loadLogs(logDays);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleJob = async (job) => {
        const newValue = !job.enabled;
        setSavingJob(job.name);
        setErrorMessage("");
        try {
            const headers = await authHeaders();
            await axios.put(`${apiPath}/${job.name}`, { enabled: newValue }, headers);
            setJobs((prev) => prev.map((j) => (j.name === job.name ? { ...j, enabled: newValue } : j)));
            loadLogs(logDays);
        } catch (error) {
            setErrorMessage(error.response?.data || error.message || "Failed to update cron job.");
        } finally {
            setSavingJob(null);
        }
    };

    const runOnce = async (job) => {
        setRunningJob(job.name);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const headers = await authHeaders();
            await axios.post(`${apiPath}/${encodeURIComponent(job.name)}/run`, {}, headers);
            setStatusMessage(`Started a one-off run of "${job.displayName || job.name}". Refresh logs to see progress.`);
            setTimeout(() => loadLogs(logDays), 1000);
        } catch (error) {
            setErrorMessage(error.response?.data || error.message || "Failed to start one-off run.");
        } finally {
            setRunningJob(null);
        }
    };

    const purgeLogs = async () => {
        const days = Number(purgeDays) || 0;
        const confirmMsg = days <= 0
            ? "Delete ALL job logs? This cannot be undone."
            : `Delete job logs older than ${days} days? This cannot be undone.`;
        if (!window.confirm(confirmMsg)) return;

        setPurging(true);
        setErrorMessage("");
        setStatusMessage("");
        try {
            const headers = await authHeaders();
            const response = await axios.delete(`${apiPath}/logs?days=${days}`, headers);
            setStatusMessage(`Deleted ${response.data.deleted} log entr${response.data.deleted === 1 ? 'y' : 'ies'}.`);
            loadLogs(logDays);
        } catch (error) {
            setErrorMessage(error.response?.data || error.message || "Failed to purge logs.");
        } finally {
            setPurging(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Scheduled Jobs (Cron)</Typography>
                <Button variant="outlined" size="small" onClick={loadJobs} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            {errorMessage && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>{String(errorMessage)}</Alert>}
            {statusMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatusMessage("")}>{statusMessage}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Group</strong></TableCell>
                                <TableCell><strong>Task</strong></TableCell>
                                <TableCell><strong>Schedule (cron)</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="center"><strong>Enabled</strong></TableCell>
                                <TableCell align="center"><strong>Run Once</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">No scheduled jobs registered.</TableCell>
                                </TableRow>
                            ) : (
                                jobs.map((job) => (
                                    <TableRow key={job.name}>
                                        <TableCell>{job.groupName || job.displayName || job.name}</TableCell>
                                        <TableCell>{job.displayName || job.name}</TableCell>
                                        <TableCell><code>{job.cronExpression}</code></TableCell>
                                        <TableCell>
                                            <Chip
                                                label={job.enabled ? "Enabled" : "Disabled"}
                                                color={job.enabled ? "success" : "default"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {savingJob === job.name ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Switch
                                                    checked={job.enabled}
                                                    onChange={() => toggleJob(job)}
                                                    color="primary"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={!job.canRunOnce || runningJob === job.name}
                                                onClick={() => runOnce(job)}
                                            >
                                                {runningJob === job.name ? "Starting..." : "Run now"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6">Job Logs</Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <TextField
                        label="Show past (days)"
                        type="number"
                        size="small"
                        value={logDays}
                        onChange={(e) => setLogDays(e.target.value)}
                        sx={{ width: 140 }}
                    />
                    <Button variant="outlined" size="small" onClick={() => loadLogs(logDays)} disabled={logsLoading}>
                        Load logs
                    </Button>
                    <TextField
                        label="Delete older than (days)"
                        type="number"
                        size="small"
                        value={purgeDays}
                        onChange={(e) => setPurgeDays(e.target.value)}
                        helperText="0 = delete all"
                        sx={{ width: 180 }}
                    />
                    <Button variant="contained" color="error" size="small" onClick={purgeLogs} disabled={purging}>
                        {purging ? "Deleting..." : "Clear logs"}
                    </Button>
                </Stack>
            </Box>

            {logsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>When</strong></TableCell>
                                <TableCell><strong>Job</strong></TableCell>
                                <TableCell><strong>Event</strong></TableCell>
                                <TableCell><strong>Message</strong></TableCell>
                                <TableCell align="right"><strong>Duration (s)</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No logs for the selected period.</TableCell>
                                </TableRow>
                            ) : (
                                logs.map((log) => {
                                    const evt = EVENT_TYPE_LABELS[log.eventType] || { label: String(log.eventType), color: 'default' };
                                    return (
                                        <TableRow key={log.id}>
                                            <TableCell style={{ whiteSpace: 'nowrap' }}>{new Date(log.loggedOn + "Z").toLocaleString()}</TableCell>
                                            <TableCell>{log.displayName || log.jobName}</TableCell>
                                            <TableCell>
                                                <Chip label={evt.label} color={evt.color} size="small" />
                                            </TableCell>
                                            <TableCell>{log.message}</TableCell>
                                            <TableCell align="right">{log.durationSeconds != null ? log.durationSeconds.toFixed(1) : ""}</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
